/**
 * Thin fetch wrapper used by all API calls.
 *
 * - Prepends the API base URL
 * - Adds Authorization header (or X-Mock-User in dev) via the supplied
 *   `getAuthHeaders` callback so this stays decoupled from MSAL
 * - Parses JSON and throws structured errors that React Query can use
 */
import { env } from './env.js';

export class ApiError extends Error {
  readonly status: number;
  readonly code: string;
  readonly details?: unknown;
  constructor(opts: { status: number; code: string; message: string; details?: unknown }) {
    super(opts.message);
    this.status = opts.status;
    this.code = opts.code;
    this.details = opts.details;
  }
}

export type AuthHeaderProvider = () => Promise<Record<string, string>> | Record<string, string>;

let authHeaderProvider: AuthHeaderProvider = () => ({});

export function setAuthHeaderProvider(provider: AuthHeaderProvider): void {
  authHeaderProvider = provider;
}

/**
 * The API tells us — with this exact machine code — that the bearer was an
 * email one-time-code session that no longer verifies (its 30-day TTL ran out,
 * or it was invalidated). It is the ONLY auth failure on which the SPA drops
 * its stored session: a bare 401 must never do that, or an Entra user would be
 * logged out by a transient token-refresh hiccup.
 */
export const OTP_SESSION_EXPIRED_CODE = 'OTP_SESSION_EXPIRED';

export type AuthFailureHandler = (error: ApiError) => void;

let authFailureHandler: AuthFailureHandler = () => undefined;

/**
 * Register what to do when the API reports a dead OTP session (see
 * OTP_SESSION_EXPIRED_CODE). Mirrors setAuthHeaderProvider so this module stays
 * decoupled from MSAL / auth state; AuthProvider wires the real handler.
 */
export function setAuthFailureHandler(handler: AuthFailureHandler): void {
  authFailureHandler = handler;
}

/** Fire the failure handler for a dead-OTP-session error; never let it mask the error. */
function notifyAuthFailure(err: ApiError): void {
  if (err.code !== OTP_SESSION_EXPIRED_CODE) return;
  try {
    authFailureHandler(err);
  } catch {
    /* the original ApiError is what callers must see */
  }
}

/**
 * Resolve the current auth headers (mock X-Mock-User or real MSAL bearer).
 * Use this for raw multipart fetches that bypass apiCall, so they carry the
 * same auth as every other request instead of a mock-only header.
 */
export async function getAuthHeaders(): Promise<Record<string, string>> {
  return Promise.resolve(authHeaderProvider());
}

export interface ApiCallOptions extends Omit<RequestInit, 'body'> {
  body?: unknown;
  /**
   * Abort after this many ms and throw a retryable TIMEOUT ApiError. Default:
   * no timeout — long ops (chat, PDF extract, uploads) must not be cut off.
   * Set on the bootstrap `/me` call so the app can't hang on a held connection
   * while the API is restarting mid-deploy.
   */
  timeoutMs?: number;
}

/**
 * fetch with an optional abort timeout. On timeout, throws a retryable ApiError
 * (status 0, code TIMEOUT) so the caller's retry logic can ride out a brief
 * API outage instead of waiting on a connection that never answers.
 */
async function fetchWithTimeout(
  url: string,
  init: RequestInit,
  timeoutMs?: number,
): Promise<Response> {
  if (!timeoutMs || timeoutMs <= 0) return fetch(url, init);
  const controller = new AbortController();
  const external = init.signal ?? undefined;
  if (external) {
    if (external.aborted) controller.abort();
    else external.addEventListener('abort', () => controller.abort(), { once: true });
  }
  let timedOut = false;
  const timer = setTimeout(() => {
    timedOut = true;
    controller.abort();
  }, timeoutMs);
  try {
    return await fetch(url, { ...init, signal: controller.signal });
  } catch (err) {
    if (timedOut) {
      throw new ApiError({
        status: 0,
        code: 'TIMEOUT',
        message: 'The server isn’t responding — it may be updating.',
      });
    }
    throw err;
  } finally {
    clearTimeout(timer);
  }
}

export async function apiCall<T = unknown>(
  path: string,
  options: ApiCallOptions = {},
): Promise<T> {
  const { timeoutMs, ...fetchInit } = options;
  const authHeaders = await Promise.resolve(authHeaderProvider());
  const headers: Record<string, string> = {
    ...authHeaders,
    ...(fetchInit.headers as Record<string, string> | undefined),
  };
  // Only set application/json when we actually have a JSON body. Fastify
  // (correctly) rejects a request with content-type=json and no body.
  if (
    options.body !== undefined &&
    options.body !== null &&
    !(options.body instanceof FormData)
  ) {
    headers['Content-Type'] = 'application/json';
  }
  const init: RequestInit = {
    ...fetchInit,
    headers,
    body:
      options.body === undefined || options.body === null
        ? undefined
        : options.body instanceof FormData
          ? options.body
          : JSON.stringify(options.body),
  };

  const url = path.startsWith('http')
    ? path
    : `${env.API_BASE_URL}${path.startsWith('/') ? path : `/${path}`}`;

  const res = await fetchWithTimeout(url, init, timeoutMs);

  if (res.status === 204) return undefined as T;

  const ct = res.headers.get('content-type') ?? '';
  const isJson = ct.includes('application/json');
  const payload = isJson ? await res.json() : await res.text();

  if (!res.ok) {
    const errPayload = (isJson ? payload : { error: { message: payload } }) as {
      error?: { code?: string; message?: string; details?: unknown };
    };
    const err = new ApiError({
      status: res.status,
      code: errPayload.error?.code ?? 'HTTP_ERROR',
      message: errPayload.error?.message ?? `HTTP ${res.status}`,
      details: errPayload.error?.details,
    });
    notifyAuthFailure(err);
    throw err;
  }
  return payload as T;
}

/**
 * Authed GET that returns the response body as a Blob (e.g. a PDF). Carries the
 * same auth headers as apiCall, so it works for role-gated download endpoints
 * that an <a href> couldn't authenticate.
 */
export async function apiFetchBlob(path: string): Promise<Blob> {
  const authHeaders = await Promise.resolve(authHeaderProvider());
  const url = path.startsWith('http')
    ? path
    : `${env.API_BASE_URL}${path.startsWith('/') ? path : `/${path}`}`;
  const res = await fetch(url, { headers: authHeaders });
  if (!res.ok) {
    let message = `HTTP ${res.status}`;
    let code = 'HTTP_ERROR';
    try {
      const j = (await res.json()) as { error?: { code?: string; message?: string } };
      message = j?.error?.message ?? message;
      code = j?.error?.code ?? code;
    } catch {
      /* non-JSON error body */
    }
    const err = new ApiError({ status: res.status, code, message });
    notifyAuthFailure(err);
    throw err;
  }
  return res.blob();
}
